import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate, useBlocker } from 'react-router-dom';
import { Box, Typography, Button, CircularProgress, IconButton, useMediaQuery, useTheme, Divider } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import LanguageIcon from '@mui/icons-material/Language';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import PlaceIcon from '@mui/icons-material/Place';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { atom_myToursState, atom_myToursLoadingState, atom_myToursError } from '@app/shared/state/my-tours.atom';
import { saveCredentials } from '@app/shared/state/credential-items.atom';
import { atom_isDesktop, atom_tourState } from '@app/shared/state/tour.atom';
import { mobilePlatform, isPwaInstalled } from '@app/shared/utils/env';
import { atom_tourStage } from '@app/shared/state/loading.atom';
import { service_tourService } from '@app/shared/services/tours.service';
import { UserTourSummary } from '@app/shared/models/user-tours.type';
import { isTourDownloaded, isTourDownloadStale } from '@app/pages/delete-dbs';

// Font scaling is now handled by CSS zoom on .App container (see AccessibilityModalContent)

// Design colors from HTML template
const COLORS = {
	primary: '#E65100',
	primaryLight: '#FF8A50',
	bgColor: '#DCDCE4',
	surface: '#ffffff',
	textDark: '#111827',
	textMedium: '#4B5563',
	textLight: '#9CA3AF',
	border: '#E5E7EB',
	success: '#10B981',
	tipBg: '#FFFBEB',
	tipBorder: '#FDE68A',
	tipText: '#B45309',
	tipLabel: '#92400E',
};

// Helper to detect if text contains Hebrew characters
const containsHebrew = (text: string): boolean => {
	const hebrewRegex = /[\u0590-\u05FF]/;
	return hebrewRegex.test(text);
};

// Helper to get initials from name
const getInitials = (name: string): string => {
	if (!name) return '??';
	const parts = name.trim().split(' ');
	if (parts.length >= 2) {
		return (parts[0][0] + parts[1][0]).toUpperCase();
	}
	return name.substring(0, 2).toUpperCase();
};

// Helper to load cached tours synchronously
const loadCachedTours = (uID: string | undefined) => {
	if (!uID) return null;
	const cacheKey = `my_tours_cache_${uID}`;
	const cached = localStorage.getItem(cacheKey);
	if (cached) {
		try {
			return JSON.parse(cached);
		} catch (e) {
			console.warn('[MyToursPage] Failed to parse cached tours:', e);
		}
	}
	return null;
};

// Helper to get saved tour ID from credentials (only if uID matches)
const getSavedTourId = (currentUID: string | undefined): string | null => {
	if (!currentUID) return null;
	const raw = localStorage.getItem('credential_items');
	if (raw) {
		try {
			const credentials = JSON.parse(raw);
			// Only return tID if the saved uID matches the current user
			if (credentials.uID === currentUID) {
				return credentials.tID || null;
			}
			return null;
		} catch (e) {
			return null;
		}
	}
	return null;
};

// Helper to cache MyTours assets for offline use
const cacheMyToursAssets = async (assets: string[]): Promise<void> => {
	if (!assets || assets.length === 0 || !('caches' in window)) return;

	const cacheName = 'firebase-storage-cache';

	try {
		const cache = await caches.open(cacheName);

		const cachePromises = assets.map(async (url) => {
			try {
				let assetUrl = url?.split('&token=')?.[0];
				if (!assetUrl) return;

				if (assetUrl.startsWith('http://')) {
					assetUrl = assetUrl.replace('http://', 'https://');
				}

				const existing = await cache.match(assetUrl);
				if (existing) return;

				const response = await fetch(assetUrl, {
					mode: 'cors',
					credentials: 'omit',
				});

				if (response.ok) {
					await cache.put(assetUrl, response.clone());
				}
			} catch (err) {
				console.warn('[MyToursPage] Failed to cache asset:', url, err);
			}
		});

		await Promise.allSettled(cachePromises);
		console.log('[MyToursPage] Cached', assets.length, 'assets for offline use');
	} catch (err) {
		console.warn('[MyToursPage] Failed to cache assets:', err);
	}
};

// Helper to check if language is Hebrew
const isHebrewLanguage = (lang: string | null | undefined): boolean => {
	if (!lang) return false;
	const lowerLang = lang.toLowerCase();
	return lowerLang === 'he' || lowerLang === 'hebrew' || lowerLang === 'עברית';
};

// Map tour language value to i18n locale code
const toLocaleCode = (lang: string | null | undefined): string => {
	if (!lang) return 'en';
	const l = lang.toLowerCase();
	if (l === 'he' || l === 'hebrew' || l === 'עברית') return 'he';
	if (l === 'fr' || l === 'french' || l === 'français') return 'fr';
	if (l === 'pt' || l === 'portuguese' || l === 'português') return 'pt';
	return 'en';
};

export const MyToursPage: React.FC = () => {
	const { t, i18n } = useTranslation();
	const { uID } = useParams<{ uID: string }>();
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const gID = searchParams.get('gID') || undefined;
	const langParam = searchParams.get('lang'); // URL language parameter

	// Block mobile back button navigation from MyTours to a tour page
	const blocker = useBlocker(({ nextLocation, historyAction }) => {
		return historyAction === 'POP' && nextLocation.pathname.startsWith('/tour/');
	});

	useEffect(() => {
		if (blocker.state === 'blocked') {
			if (window.confirm(t('on_tour.go_back_tour') as string)) {
				// Reset tour state so the tour fully reloads (same as entering a new tour)
				setTourState(undefined);
				setTourStage('loading');
				blocker.proceed();
			} else {
				blocker.reset();
			}
		}
	}, [blocker.state]);

	// Load cache synchronously on first render
	const cachedData = loadCachedTours(uID);
	const cachedLang = cachedData?.lang || cachedData?.tours?.[0]?.language;

	// Set language synchronously so first render uses correct lang
	const targetLang = !uID
		? (isHebrewLanguage(langParam) ? 'he' : 'en')
		: (isHebrewLanguage(cachedLang) ? 'he' : 'en');
	if (i18n.language !== targetLang) {
		i18n.changeLanguage(targetLang);
		document.dir = targetLang === 'he' ? 'rtl' : 'ltr';
	}

	// Check if there's a saved tour to go back to (for this specific user)
	const savedTourId = getSavedTourId(uID);

	const [tours, setTours] = useRecoilState(atom_myToursState);
	const [loadingState, setLoadingState] = useRecoilState(atom_myToursLoadingState);
	const [error, setError] = useRecoilState(atom_myToursError);

	// Media query for desktop detection (more reliable than atom for this page)
	const theme = useTheme();
	const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

	const setTourStage = useSetRecoilState(atom_tourStage);
	const setTourState = useSetRecoilState(atom_tourState);

	const [foundUserId, setFoundUserId] = useState<string>(cachedData?.foundUserId || '');
	const [generalColor, setGeneralColor] = useState<string | undefined>(cachedData?.generalColor);

	// UI State
	const [tipDismissed, setTipDismissed] = useState(() =>
		sessionStorage.getItem('tipDismissed') === 'true'
	);

	// Login State (when no uID in URL)
	const [loginInput, setLoginInput] = useState('');

	// PWA Installation State (shared with TourStartPage)
	const [deferredInstallPrompt, setDeferredInstallPrompt] = useState<any>(null);
	const [canInstallPwa, setCanInstallPwa] = useState(false);
	const [showIosInstallGuide, setShowIosInstallGuide] = useState(false);
	const [pwaBannerDismissed, setPwaBannerDismissed] = useState(false);

	// Offline detection
	const [isOffline, setIsOffline] = useState(!navigator.onLine);
	useEffect(() => {
		const goOffline = () => setIsOffline(true);
		const goOnline = () => setIsOffline(false);
		window.addEventListener('offline', goOffline);
		window.addEventListener('online', goOnline);
		return () => {
			window.removeEventListener('offline', goOffline);
			window.removeEventListener('online', goOnline);
		};
	}, []);

	// Language is set synchronously above (before render) to avoid flash of wrong language

	// PWA Install prompt listener (shared global pattern)
	useEffect(() => {
		console.log('[PWA Debug MyTours] Setting up beforeinstallprompt listener');

		// Check if event was already captured globally before React mounted
		if ((window as any).deferredInstallPrompt) {
			console.log('[PWA Debug MyTours] Found globally captured install prompt');
			setDeferredInstallPrompt((window as any).deferredInstallPrompt);
			setCanInstallPwa(true);
		}

		const handler = (e: any) => {
			console.log('[PWA Debug MyTours] beforeinstallprompt event fired!', e);
			e.preventDefault();
			setDeferredInstallPrompt(e);
			setCanInstallPwa(true);
			// Store globally for sharing between pages
			(window as any).deferredInstallPrompt = e;
		};
		window.addEventListener('beforeinstallprompt', handler);

		// Check if already installed
		if (isPwaInstalled) {
			console.log('[PWA Debug MyTours] App is already installed (standalone mode)');
		}

		return () => window.removeEventListener('beforeinstallprompt', handler);
	}, []);

	// Handle PWA install click
	const handlePwaInstall = async () => {
		try {
			if (deferredInstallPrompt) {
				deferredInstallPrompt.prompt();
				const choiceResult = await deferredInstallPrompt.userChoice;
				if (choiceResult.outcome === 'accepted') {
					setCanInstallPwa(false);
					console.log('[PWA MyTours] App installed successfully');
				}
			}
		} catch (e) {
			console.error('[PWA MyTours] Install failed', e);
		}
	};

	// Dismiss tip banner
	const handleDismissTip = () => {
		setTipDismissed(true);
		sessionStorage.setItem('tipDismissed', 'true');
	};

	// Handle login form submission
	const handleLogin = () => {
		if (!loginInput.trim()) return;
		// Navigate to mytours with uID - existing logic will handle API call and credential saving
		navigate(`/mytours/${encodeURIComponent(loginInput.trim())}${gID ? `?gID=${gID}` : ''}`);
	};

	// Reset state when uID changes (e.g. switching accounts)
	useEffect(() => {
		setTours([]);
		setFoundUserId('');
		setLoadingState('idle');
		setError(null);
	}, [uID]);

	// Initialize from cache on mount
	useEffect(() => {
		if (!uID) return;

		const cacheKey = `my_tours_cache_${uID}`;
		const cached = localStorage.getItem(cacheKey);

		if (cached) {
			try {
				const parsedCache = JSON.parse(cached);
				if (parsedCache?.tours && parsedCache.tours.length > 0) {
					setTours(parsedCache.tours);
					if (parsedCache.foundUserId) {
						setFoundUserId(parsedCache.foundUserId);
					}
					setLoadingState('success');
				}
			} catch (e) {
				console.warn('[MyToursPage] Failed to parse cache:', e);
			}
		}
	}, [uID]);

	// Save uID to credential_items if not already saved (for PWA redirect)
	useEffect(() => {
		if (!uID) return;

		const raw = localStorage.getItem('credential_items');
		const credentials = raw ? JSON.parse(raw) : {};

		if (!credentials.uID || credentials.uID !== uID) {
			const newCredentials = { ...credentials, uID };
			if (gID) newCredentials.gID = gID;
			saveCredentials(newCredentials);
		}
	}, [uID, gID]);

	// Fetch fresh tours from API
	useEffect(() => {
		const fetchTours = async () => {
			if (!uID) return;

			const cacheKey = `my_tours_cache_${uID}`;
			const hasCachedData = localStorage.getItem(cacheKey) !== null;
			if (!hasCachedData) {
				setLoadingState('loading');
			}
			setError(null);

			try {
				const data = await service_tourService.getUserTours(uID, gID);
				const toursList = Array.isArray(data) ? data : (data?.tours || []);

				if (toursList.length === 0) {
					setTours([]);
					setLoadingState('success');
					return;
				}

				setTours(toursList);

				const newFoundUserId = data?.foundUserId || '';
				if (newFoundUserId) {
					setFoundUserId(newFoundUserId);
				}

				const newGeneralColor = data?.meta?.generalColor;
				if (newGeneralColor) {
					setGeneralColor(newGeneralColor);
				}

				const allAssets = data?.allAssets || [];
				if (allAssets.length > 0) {
					cacheMyToursAssets(allAssets);
				}

				// Set language based on API response lang or first tour's language
				const responseLang = data?.lang || toursList[0]?.language;
				if (isHebrewLanguage(responseLang)) {
					i18n.changeLanguage('he');
					document.dir = 'rtl';
				} else {
					i18n.changeLanguage('en');
					document.dir = 'ltr';
				}

				localStorage.setItem(cacheKey, JSON.stringify({
					tours: toursList,
					foundUserId: newFoundUserId,
					lang: responseLang, // Save language to cache
					generalColor: data?.meta?.generalColor, // Save brand color to cache
					timestamp: Date.now()
				}));

				// Save known tour count so SideBar can detect new tours
				localStorage.setItem(`my_tours_known_count_${uID}`, String(toursList.length));
				// Clear any persisted badge — user is now seeing all tours
				localStorage.removeItem(`my_tours_new_count_${uID}`);

				setLoadingState('success');
			} catch (err) {
				console.error('[MyToursPage] Error fetching tours:', err);
				if (!hasCachedData) {
					setError(t('my_tours.error.title'));
					setLoadingState('error');
				} else {
					setLoadingState('success');
				}
			}
		};

		fetchTours();
	}, [uID, gID]);

	const handleTourClick = (tour: UserTourSummary) => {
		const tourId = tour.tourId || tour.id;
		if (!tourId) return;

		const raw = localStorage.getItem('credential_items');
		const credentials = raw ? JSON.parse(raw) : {};
		const effectiveUID = uID || credentials.uID;
		if (!effectiveUID) return;

		credentials.tID = tourId;
		saveCredentials(credentials);

		let url = `/tour/${tourId}?uID=${encodeURIComponent(effectiveUID)}`;
		if (gID) url += `&gID=${encodeURIComponent(gID)}`;
		setTourState(undefined);
		setTourStage('loading');
		navigate(url);
	};

	const handleDownloadTourClick = (tour: UserTourSummary) => {
		const tourId = tour.tourId || tour.id;
		if (!tourId) return;

		const raw = localStorage.getItem('credential_items');
		const credentials = raw ? JSON.parse(raw) : {};
		const effectiveUID = uID || credentials.uID;
		if (!effectiveUID) return;

		// Remove first_visit flag so the tour shows its startPage (not skip to map)
		localStorage.removeItem('first_visit_' + tourId);

		// Clear stale tour state
		setTourState(undefined);
		setTourStage('loading');

		// Save credentials with tourId
		credentials.tID = tourId;
		saveCredentials(credentials);

		// Navigate to tour with autoDownload flag
		let url = `/tour/${tourId}?uID=${encodeURIComponent(effectiveUID)}`;
		if (gID) url += `&gID=${encodeURIComponent(gID)}`;
		url += '&autoDownload=true';
		navigate(url);
	};

	const handleRetry = () => {
		setLoadingState('idle');
		setError(null);
		window.location.reload();
	};

	// Use cached data for display if available
	const displayTours: UserTourSummary[] = tours.length > 0 ? tours : (cachedData?.tours || []);
	const displayFoundUserId = foundUserId || cachedData?.foundUserId || '';

	// Show loading until we have tours data
	const isLoading = (loadingState === 'loading' || loadingState === 'idle') && displayTours.length === 0;

	// Detect RTL for the page based on current language
	const isPageRTL = i18n.language === 'he';

	// Login View (when no uID in URL)
	if (!uID) {
		return (
			<Box
				dir={isPageRTL ? 'rtl' : 'ltr'}
				sx={{
					minHeight: '100vh',
					backgroundColor: '#FFFFFF',
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
				}}
			>
				<Box
					sx={{
						width: '100%',
						maxWidth: 420,
						p: 3,
						display: 'flex',
						flexDirection: 'column',
						textAlign: 'center',
						minHeight: '100vh',
					}}
				>

					<Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', pt: '10vh' }}>
						{/* Icon */}
						<Box
							sx={{
								width: 88,
								height: 88,
								padding: '20px',
								gap: '10px',
								backgroundColor: '#FBE9DD',
								borderRadius: '44px',
								margin: '0 auto 24px auto',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								boxSizing: 'border-box',
							}}
						>
							<LocationOnIcon sx={{ fontSize: 48, color: generalColor || '#E65100' }} />
						</Box>

						{/* Title */}
						<Typography
							sx={{
								fontSize: 18,
								fontWeight: 700,
								lineHeight: '22px',
								letterSpacing: 0,
								textAlign: 'center',
								color: '#000',
								mb: 1,
							}}
						>
							{t('my_tours.login.title')}
						</Typography>
						<Typography
							sx={{
								fontSize: 16,
								fontWeight: 400,
								lineHeight: '24px',
								letterSpacing: 0,
								textAlign: 'center',
								color: '#6B7280',
								mb: 4,
							}}
						>
							{t('my_tours.login.subtitle')}
						</Typography>

						{/* Card Container */}
						<Box sx={{
							backgroundColor: '#F3F4F6',
							borderRadius: '16px',
							p: 3,
							mb: 4,
							border: '1px solid #E5E7EB'
						}}>
							{/* Input Field */}
							<Box sx={{ mb: 2.5, textAlign: 'left' }}>
								<Typography
									sx={{
										fontSize: 14,
										fontWeight: 400,
										lineHeight: '20px',
										letterSpacing: 0,
										mb: 1,
										color: '#6B7280',
									}}
								>
									{t('my_tours.login.input_label')}
								</Typography>
								<Box sx={{ position: 'relative' }}>
									<Box
										component="input"
										type="text"
										value={loginInput}
										onChange={(e: any) => setLoginInput(e.target.value)}
										onKeyDown={(e: any) => {
											if (e.key === 'Enter') handleLogin();
										}}
										placeholder={t('my_tours.login.input_placeholder') as string}
										sx={{
											width: '100%',
											padding: '14px',
											paddingRight: '40px',
											border: '1px solid #E5E7EB',
											borderRadius: '12px',
											fontSize: 16,
											backgroundColor: '#fff',
											boxSizing: 'border-box',
											outline: 'none',
											color: '#111827',
											fontWeight: 500,
											transition: 'all 0.2s ease-in-out',
											'&::placeholder': {
												color: '#9CA3AF',
												fontWeight: 400
											},
											'&:focus': {
												borderColor: generalColor || '#E65100',
												// boxShadow: '0 0 0 1px brand'
											}
										}}
									/>
									<HelpOutlineIcon
										sx={{
											position: 'absolute',
											right: 12,
											top: '50%',
											transform: 'translateY(-50%)',
											color: '#9CA3AF',
											fontSize: 20
										}}
									/>
								</Box>
								<Typography
									sx={{
										fontSize: 12,
										fontWeight: 400,
										lineHeight: '16px',
										letterSpacing: 0,
										color: '#6B7280',
										mt: 1,
									}}
								>
									{t('my_tours.login.input_hint')}
								</Typography>
							</Box>

							{/* Submit Button */}
							<Button
								fullWidth
								onClick={handleLogin}
								disabled={!loginInput.trim()}
								sx={{
									fontSize: 14,
									fontWeight: 600,
									lineHeight: '16px',
									letterSpacing: 0,
									verticalAlign: 'bottom',
									backgroundColor: generalColor || '#E65100',
									color: '#fff',
									py: 1.5,
									borderRadius: '24px',
									textTransform: 'none',
									boxShadow: 'none',
									'&:hover': {
										backgroundColor: generalColor || '#D34600',
										opacity: 0.9,
										boxShadow: 'none',
									},
									'&:disabled': {
										backgroundColor: generalColor || '#F3D2B8',
										color: '#fff',
										opacity: 0.5,
									},
								}}
							>
								{t('my_tours.login.submit_button')}
							</Button>
						</Box>
					</Box>

					{/* Footer */}
					<Box sx={{ textAlign: 'center', pb: 2 }}>
						<Typography sx={{ fontSize: 14, color: '#6B7280' }}>
							{t('my_tours.login.need_help') || 'Need help?'} <a href="mailto:support@d-guide.com" style={{ color: generalColor || '#E65100', fontWeight: 600, textDecoration: 'none' }}>{t('my_tours.login.contact_us') || 'Contact Us'}</a>
						</Typography>
					</Box>
				</Box>
			</Box>
		);
	}

	// Loading State with overlay
	if (isLoading) {
		return (
			<Box
				sx={{
					position: 'fixed',
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					backgroundColor: COLORS.bgColor,
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					justifyContent: 'center',
					zIndex: 100,
				}}
			>
				<Box
					sx={{
						width: 40,
						height: 40,
						border: `3px solid ${COLORS.border}`,
						borderTopColor: (generalColor || COLORS.primary),
						borderRadius: '50%',
						animation: 'spin 0.8s linear infinite',
						mb: 2,
						'@keyframes spin': {
							to: { transform: 'rotate(360deg)' },
						},
					}}
				/>
				<Typography sx={{ fontSize: 16, color: COLORS.textMedium, mb: 1 }}>
					{t('my_tours.loading') || 'Loading your adventures...'}
				</Typography>
				{displayFoundUserId && (
					<Typography sx={{ fontSize: 14, color: COLORS.textLight }}>
						{t('my_tours.header.welcome_back', { name: displayFoundUserId })}
					</Typography>
				)}
			</Box>
		);
	}

	// Error state
	if (loadingState === 'error' && displayTours.length === 0) {
		return (
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					justifyContent: 'center',
					minHeight: '100vh',
					gap: 2,
					px: 3,
					backgroundColor: COLORS.bgColor,
				}}
			>
				<Typography variant="h5" color="error">
					{t('my_tours.error.title')}
				</Typography>
				<Button variant="contained" onClick={handleRetry} sx={{ backgroundColor: (generalColor || COLORS.primary) }}>
					{t('my_tours.error.retry')}
				</Button>
			</Box>
		);
	}

	// Empty state
	if (loadingState === 'success' && displayTours.length === 0) {
		return (
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					justifyContent: 'center',
					minHeight: '100vh',
					gap: 2,
					px: 3,
					backgroundColor: COLORS.bgColor,
				}}
			>
				<Typography variant="h5" sx={{ color: COLORS.textDark }}>
					{t('my_tours.empty.title')}
				</Typography>
				<Typography variant="body1" sx={{ color: COLORS.textMedium, textAlign: 'center' }}>
					{t('my_tours.empty.description')}
				</Typography>
				<Button
					variant="contained"
					onClick={() => {
						localStorage.removeItem('credential_items');
						navigate('/mytours');
					}}
					sx={{
						backgroundColor: (generalColor || COLORS.primary),
						mt: 2,
						textTransform: 'none',
						'&:hover': {
							backgroundColor: (generalColor || COLORS.primaryLight),
						},
					}}
				>
					{t('my_tours.empty.back_to_login') || 'Back to Login'}
				</Button>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				minHeight: '100vh',
				backgroundColor: COLORS.bgColor,
				width: '100%',
				position: 'relative',
				zIndex: 2,
			}}
			dir={isPageRTL ? 'rtl' : 'ltr'}
		>
			{/* Header Bar - Full Width on Desktop */}
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					p: { xs: '8px 10px', md: '14px 48px' },
					backgroundColor: '#ffffff',
					position: 'sticky',
					top: 0,
					zIndex: 10,
					width: '100%',
					boxSizing: 'border-box',
					borderBottom: '0.5px solid #E5E7EB',
				}}
				dir={isPageRTL ? 'rtl' : 'ltr'}
			>
				{/* Left Group: Exit Button + Avatar + Title */}
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
					{/* Exit/Logout Button (replacing arrow) */}
					<IconButton
						onClick={() => {
							localStorage.removeItem('credential_items');
							setTours([]);
							setFoundUserId('');
							setLoadingState('idle');
							navigate('/mytours');
						}}
						sx={{ color: COLORS.textDark, p: 0.5 }}
					>
						<LogoutIcon sx={{ fontSize: { xs: 22, md: 24 } }} />
					</IconButton>

					{/* Avatar & Title Group */}
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
						<Box sx={{
							width: { xs: 38, md: 40 },
							height: { xs: 38, md: 40 },
							borderRadius: '50%',
							border: `2px solid ${(generalColor || COLORS.primary)}`,
							overflow: 'hidden',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							backgroundColor: '#fff'
						}}>
							{/* Using generic person icon if no image available */}
							<PersonIcon sx={{ fontSize: { xs: 22, md: 24 }, color: (generalColor || COLORS.primary) }} />
						</Box>

						<Typography sx={{ fontSize: 16, fontWeight: 700, color: COLORS.textDark }}>
							{t('my_tours.title') || 'My Tours'}
						</Typography>
					</Box>
				</Box>

				{/* Right Side: Back to Tour (replacing sandwich) */}
				{savedTourId ? (
					<Button
						variant="contained"
						onClick={() => {
							const raw = localStorage.getItem('credential_items');
							const creds = raw ? JSON.parse(raw) : {};
							const effectiveUID = uID || creds.uID;
							if (!effectiveUID) return;
							let url = `/tour/${savedTourId}?uID=${encodeURIComponent(effectiveUID)}`;
							if (gID) url += `&gID=${encodeURIComponent(gID)}`;
							navigate(url);
						}}
						sx={{
							backgroundColor: (generalColor || COLORS.primary),
							color: '#fff', // White text
							borderRadius: '20px',
							textTransform: 'none',
							fontWeight: 700,
							boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
							'&:hover': {
								backgroundColor: (generalColor || COLORS.primaryLight),
								opacity: 0.9,
								boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
							},
							minWidth: 'auto',
							px: 1.5,
							py: 0.5,
							fontSize: 13
						}}
					>
						{t('my_tours.header.return_to_tour')}
					</Button>
				) : (
					<Box sx={{ width: 40 }} /> /* Spacer to balance layout if needed */
				)}
			</Box>

			{/* App Container for Content */}
			<Box
				sx={{
					width: '100%',
					maxWidth: { xs: '100%', md: '1480px' },
					margin: '0 auto',
					backgroundColor: COLORS.bgColor,
					pb: 5,
				}}
			>
				{/* Content Area */}
				<Box sx={{ p: { xs: '16px', md: 0 }, px: { md: '48px' }, pt: { md: 3 } }}>
					{/* PWA Install Banner - Android (automatic prompt) */}
					{!isDesktop && canInstallPwa && mobilePlatform === 'android' && !pwaBannerDismissed && (
						<Box
							sx={{
								backgroundColor: '#fff',
								border: `1px solid ${COLORS.border}`,
								borderRadius: '16px',
								overflow: 'hidden',
								boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
								mb: '15px',
								p: 2,
								position: 'relative',
								textAlign: isPageRTL ? 'right' : 'left',
							}}
						>
							<IconButton
								size="small"
								onClick={() => setPwaBannerDismissed(true)}
								sx={{ position: 'absolute', top: 8, right: 8, color: COLORS.textLight }}
							>
								<CloseIcon fontSize="small" />
							</IconButton>

							<Typography sx={{
								fontSize: 16,
								fontWeight: 600,
								lineHeight: '20px',
								letterSpacing: 0,
								textAlign: 'center',
								color: COLORS.textDark,
								mb: 0.5
							}}>
								{t('my_tours.pwa.add_to_home')}
							</Typography>

							<Typography sx={{
								fontSize: 14,
								fontWeight: 400,
								lineHeight: '20px',
								letterSpacing: 0,
								color: COLORS.textMedium,
								mb: 2
							}}>
								{t('my_tours.pwa.add_to_home_description')}
							</Typography>

							<Button
								onClick={handlePwaInstall}
								fullWidth
								sx={{
									backgroundColor: generalColor || '#E65100',
									color: '#fff',
									borderRadius: '24px',
									py: 1,
									fontSize: 14,
									fontWeight: 600,
									textTransform: 'none',
									boxShadow: 'none',
									'&:hover': {
										backgroundColor: generalColor || '#D34600',
										opacity: 0.9,
										boxShadow: 'none',
									},
								}}
							>
								{t('my_tours.pwa.install_button')}
							</Button>
						</Box>
					)}

					{/* PWA Install Banner - iOS (manual instructions) */}
					{!isDesktop && mobilePlatform === 'ios' && !isPwaInstalled && !pwaBannerDismissed && (
						<Box
							sx={{
								backgroundColor: '#fff',
								border: `1px solid ${COLORS.border}`,
								borderRadius: '16px',
								overflow: 'hidden',
								boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
								mb: '15px',
								p: 2,
								position: 'relative',
								textAlign: isPageRTL ? 'right' : 'left',
							}}
						>
							<IconButton
								size="small"
								onClick={() => setPwaBannerDismissed(true)}
								sx={{ position: 'absolute', top: 8, right: 8, color: COLORS.textLight }}
							>
								<CloseIcon fontSize="small" />
							</IconButton>

							<Typography sx={{
								fontSize: 16,
								fontWeight: 600,
								lineHeight: '20px',
								letterSpacing: 0,
								textAlign: 'center',
								color: COLORS.textDark,
								mb: 0.5
							}}>
								{t('my_tours.pwa.add_to_home')}
							</Typography>

							<Typography sx={{
								fontSize: 14,
								fontWeight: 400,
								lineHeight: '20px',
								letterSpacing: 0,
								color: COLORS.textMedium,
								mb: 2
							}}>
								{t('my_tours.pwa.add_to_home_description')}
							</Typography>

							<Button
								onClick={() => setShowIosInstallGuide(true)}
								fullWidth
								sx={{
									backgroundColor: generalColor || '#E65100',
									color: '#fff',
									borderRadius: '24px',
									py: 1,
									fontSize: 14,
									fontWeight: 600,
									textTransform: 'none',
									boxShadow: 'none',
									'&:hover': {
										backgroundColor: generalColor || '#D34600',
										opacity: 0.9,
										boxShadow: 'none',
									},
								}}
							>
								{t('my_tours.pwa.how_to_install')}
							</Button>
						</Box>
					)}

					{/* Tip Banner - Only on Mobile */}
					{!isDesktop && !tipDismissed && (
						<Box
							sx={{
								backgroundColor: COLORS.tipBg,
								border: `1px solid ${COLORS.tipBorder}`,
								p: '12px 16px',
								borderRadius: '12px',
								mb: '10px',
								display: 'flex',
								alignItems: 'flex-start',
								gap: '10px',
								position: 'relative',
								flexDirection: isPageRTL ? 'row-reverse' : 'row',
							}}
						>
							<svg
								style={{ color: '#F59E0B', flexShrink: 0, marginTop: 1 }}
								width="18"
								height="18"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
							>
								<path d="M9 18h6M10 22h4M12 2a7 7 0 0 1 7 7c0 2.38-1.19 4.47-3 5.74V17a2 2 0 0 1-2 2h-4a2 2 0 0 1-2-2v-2.26C6.19 13.47 5 11.38 5 9a7 7 0 0 1 7-7z"></path>
							</svg>
							<Box sx={{ flex: 1, textAlign: isPageRTL ? 'right' : 'left' }}>
								<Typography component="span" sx={{ fontWeight: 600, fontSize: 13, color: COLORS.tipLabel }}>
									{t('my_tours.tip.label')}
								</Typography>
								<Typography component="span" sx={{ fontSize: 13, color: COLORS.tipText }}>
									{t('my_tours.tip.text')}
								</Typography>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
									<SmartphoneIcon sx={{ fontSize: 15, color: COLORS.tipText }} />
									<Typography sx={{ fontSize: 12, color: COLORS.tipText }}>
										{' - '}{t('my_tours.tip.devices_explanation')}
									</Typography>
								</Box>
							</Box>
							<Box
								onClick={handleDismissTip}
								sx={{
									background: 'none',
									border: 'none',
									cursor: 'pointer',
									color: '#D97706',
									p: 0,
									lineHeight: 1,
									opacity: 0.7,
									transition: 'opacity 0.2s',
									'&:hover': { opacity: 1 },
								}}
							>
								<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
									<line x1="18" y1="6" x2="6" y2="18"></line>
									<line x1="6" y1="6" x2="18" y2="18"></line>
								</svg>
							</Box>
						</Box>
					)}

					{/* Tour Grid */}
					<Box
						sx={{
							display: 'grid',
							gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
							gap: '12px',
						}}
					>
						{displayTours.map((tour: UserTourSummary, index: number) => {
							const tourId = tour.tourId || tour.id || '';
							const tourImage = tour.image || tour.tour_image;
							const tourTitle = (tour.title || t('my_tours.untitled_tour')) as string;
							const tourDescription = tour.main_sententce || tour.description || '';
							const mainColor = generalColor || '#D25529';
							const isContentRTL = containsHebrew(tourTitle) || containsHebrew(tourDescription) || isHebrewLanguage(tour.language);
							const isOfflineReady = tourId ? isTourDownloaded(tourId) : false;
							const guideName = tour.guide?.full_name || '';
							const guideInitials = getInitials(guideName);
							const tourDistance = tour.distance;
							const tourStops = tour.stop_count || tour.stops?.length;
							const tourSize = tour.sizeGB || (tour.size ? tour.size / 1000 : null); // Convert to GB if needed
							const tourLanguage = tour.language;
							const tourLng = { lng: toLocaleCode(tourLanguage) };
							const usedDevices = tour.usage;
							const maxDevices = tour.maxUsage;
							const hasUsageData = usedDevices !== undefined && maxDevices !== undefined;
							const isCurrentTour = !!(savedTourId && tourId && String(tourId) === String(savedTourId));

							return (
								<Box
									key={tourId || index}
									sx={{
										backgroundColor: COLORS.surface,
										borderRadius: '16px',
										overflow: 'hidden',
										boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
										border: '1px solid rgba(0,0,0,0.03)',
										display: 'flex',
										flexDirection: 'column',
										animation: `fadeInUp 0.6s cubic-bezier(0.2, 0.8, 0.2, 1) forwards`,
										animationDelay: `${0.1 + index * 0.1}s`,
										opacity: 0,
										minHeight: 400,
										transform: 'translateY(20px)',
										'@keyframes fadeInUp': {
											to: { opacity: 1, transform: 'translateY(0)' },
										},
									}}
								>
									{/* Tour Image Container with Padding */}
									<Box sx={{ p: 1.5, pb: 0 }}>
										<Box sx={{
											height: 180,
											borderRadius: '16px',
											overflow: 'hidden',
											position: 'relative',
											backgroundColor: '#eee'
										}}>
											{tourImage ? (
												<img
													src={tourImage}
													alt={tourTitle}
													style={{
														width: '100%',
														height: '100%',
														objectFit: 'cover',
													}}
												/>
											) : (
												<Box
													sx={{
														width: '100%',
														height: '100%',
														display: 'flex',
														alignItems: 'center',
														justifyContent: 'center',
														backgroundColor: '#ddd',
														color: COLORS.textLight,
													}}
												>
													{t('my_tours.tour_card.no_image', tourLng)}
												</Box>
											)}
											{/* Status Badge Over Image */}
											<Box
												sx={{
													position: 'absolute',
													top: 0,
													right: 0,
													zIndex: 10,
												}}
											>
												{isDesktop ? (
													// Desktop: Offline not available
													<Box sx={{
														color: '#fff',
														fontSize: 12,
														fontWeight: 400,
														lineHeight: '16px',
														letterSpacing: 0,
														backgroundColor: 'rgba(0,0,0,0.7)',
														backdropFilter: 'blur(4px)',
														px: 2,
														py: 1,
														borderBottomLeftRadius: '16px',
														borderTopRightRadius: '16px',
														display: 'flex',
														alignItems: 'center',
														gap: 1,
													}} dir={isContentRTL ? 'rtl' : 'ltr'}>
														{t('my_tours.tour_card.offline_not_available_desktop', tourLng)}
													</Box>
												) : isOfflineReady && isTourDownloadStale(tourId) ? (
													// Mobile: Offline ready but stale (>7 days)
													<Box sx={{
														color: '#fff',
														fontSize: 12,
														fontWeight: 400,
														lineHeight: '16px',
														letterSpacing: 0,
														backgroundColor: '#7F1D1D',
														pl: 1.5,
														pr: 2,
														py: 1,
														borderBottomLeftRadius: '16px',
														borderTopRightRadius: '16px',
														display: 'flex',
														alignItems: 'center',
														gap: 1,
													}} dir={isContentRTL ? 'rtl' : 'ltr'}>
														<Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#EF4444', flexShrink: 0 }} />
														{t('my_tours.tour_card.download_stale', tourLng)}
													</Box>
												) : isOfflineReady ? (
													// Mobile: Offline ready (fresh)
													<Box sx={{
														color: '#fff',
														fontSize: 12,
														fontWeight: 400,
														lineHeight: '16px',
														letterSpacing: 0,
														backgroundColor: '#4A1C40',
														pl: 1.5,
														pr: 2,
														py: 1,
														borderBottomLeftRadius: '16px',
														borderTopRightRadius: '16px',
														display: 'flex',
														alignItems: 'center',
														gap: 1,
													}} dir={isContentRTL ? 'rtl' : 'ltr'}>
														<CheckCircleIcon sx={{ fontSize: 16, color: '#fff' }} />
														{t('my_tours.tour_card.offline_ready', tourLng)}
													</Box>
												) : (
													// Mobile: Online only
													<Box sx={{
														color: '#fff',
														fontSize: 12,
														fontWeight: 400,
														lineHeight: '16px',
														letterSpacing: 0,
														backgroundColor: 'rgba(0,0,0,0.7)',
														backdropFilter: 'blur(4px)',
														px: 2,
														py: 1,
														borderBottomLeftRadius: '16px',
														borderTopRightRadius: '16px',
														display: 'flex',
														alignItems: 'center',
														gap: 1,
													}} dir={isContentRTL ? 'rtl' : 'ltr'}>
														{t('my_tours.tour_card.online_only', tourLng)}
													</Box>
												)}
											</Box>
										</Box>
									</Box>

									{/* Tour Content */}
									<Box
										sx={{
											p: 2,
											display: 'flex',
											flexDirection: 'column',
											gap: 0.5,
											flex: 1 // Allow content to grow and fill the card
										}}
										dir={isContentRTL ? 'rtl' : 'ltr'}
									>
										{/* Creator Info Row */}
										<Box dir={isContentRTL ? 'rtl' : 'ltr'} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
											{/* Avatar */}
											{tour.guide?.profile_image ? (
												<img
													src={tour.guide.profile_image}
													alt="Guide"
													style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', border: '1px solid #eee' }}
												/>
											) : (
												<Box sx={{ width: 40, height: 40, borderRadius: '50%', backgroundColor: '#000', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700 }}>
													{guideInitials}
												</Box>
											)}
											<Box sx={{ display: 'flex', flexDirection: 'column' }}>
												<Typography sx={{
													fontSize: 14,
													fontWeight: 400,
													lineHeight: '20px',
													letterSpacing: 0,
													textAlign: 'center',
													color: COLORS.textDark
												}}>
													{t('my_tours.tour_card.created_by', { name: guideName || 'Guide', ...tourLng })}
												</Typography>
												{/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexDirection: isContentRTL ? 'row-reverse' : 'row' }}>
													<Typography sx={{ fontSize: 12, fontWeight: 700, color: COLORS.textDark }}>4.5</Typography>
													<Box sx={{ display: 'flex' }}>
														{[1, 2, 3, 4, 5].map(i => (
															<StarIcon key={i} sx={{ fontSize: 14, color: i === 5 ? '#E5E7EB' : '#F59E0B' }} />
														))}
													</Box>
													<Typography sx={{ fontSize: 12, color: COLORS.textLight, ml: 0.5 }}>
														• 200 {isContentRTL ? 'ביקורות' : 'reviews'}
													</Typography>
												</Box> */}
											</Box>
										</Box>

										{/* Divider between company name and tour name */}
										<Divider /* sx={{ my: 1.5 }} */ />

										<Box sx={{ padding: '8px' }} dir={isContentRTL ? 'rtl' : 'ltr'}>
											{/* Title */}
											<Typography
												sx={{
													fontSize: 16,
													fontWeight: 600,
													lineHeight: '20px',
													letterSpacing: 0,
													color: COLORS.textDark,
													mb: '4px',
												}}
											>
												{tourTitle}
											</Typography>

											{/* Description */}
											<Typography
												sx={{
													fontSize: 14,
													fontWeight: 400,
													lineHeight: '20px',
													letterSpacing: 0,
													color: COLORS.textMedium,
													display: '-webkit-box',
													WebkitLineClamp: 2,
													WebkitBoxOrient: 'vertical',
													overflow: 'hidden',
													mb: '8px',
												}}
											>
												{tourDescription}
											</Typography>


											{/* Spacer to push details and button to bottom */}
											<Box sx={{ flexGrow: 1 }} />

											{/* Metadata Row with Icons */}
											<Box dir={isContentRTL ? 'rtl' : 'ltr'} sx={{
												display: 'flex',
												alignItems: 'center',
												gap: 1.5,
												// mb: 2,
												color: COLORS.textMedium,
											}}>
												{/* Stops */}
												<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
													<PlaceIcon sx={{ fontSize: 16, color: COLORS.textLight }} />
													<Typography sx={{
														fontSize: 12,
														fontWeight: 700,
														lineHeight: '16px',
														letterSpacing: 0
													}}>{tourStops || 15} {t('my_tours.tour_card.stops', tourLng)}</Typography>
												</Box>
												<Typography sx={{ color: COLORS.border }}>•</Typography>

												{/* Language */}
												<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
													<LanguageIcon sx={{ fontSize: 16, color: COLORS.textLight }} />
													<Typography sx={{
														fontSize: 12,
														fontWeight: 700,
														lineHeight: '16px',
														letterSpacing: 0
													}}>{tourLanguage || 'English'}</Typography>
												</Box>
												<Typography sx={{ color: COLORS.border }}>•</Typography>

												{/* Devices */}
												<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
													<SmartphoneIcon sx={{ fontSize: 16, color: COLORS.textLight }} />
													<Typography sx={{
														fontSize: 12,
														fontWeight: 700,
														lineHeight: '16px',
														letterSpacing: 0
													}}>{usedDevices ?? 2}/{maxDevices ?? 0} {t('my_tours.tour_card.devices', tourLng)}</Typography>
												</Box>
											</Box>
										</Box>

										<Divider sx={{ mb: 1 }} />

										{/* Start Button - hidden when offline and tour not downloaded */}
										{!(isOffline && !isOfflineReady) && (
											<Button
												fullWidth
												onClick={() => handleTourClick(tour)}
												dir={isContentRTL ? 'rtl' : 'ltr'}
												sx={{
													fontSize: 16,
													fontWeight: 700,
													lineHeight: '16px',
													letterSpacing: 0,
													verticalAlign: 'bottom',
													backgroundColor: mainColor,
													color: '#fff',
													borderRadius: '24px',
													py: 1.5,
													textTransform: 'none',
													boxShadow: 'none',
													mt: 0,
													transition: 'all 0.2s',
													'&:hover': {
														backgroundColor: mainColor,
														opacity: 0.9,
														transform: 'scale(1.02)'
													}
												}}
											>
												{isCurrentTour ? t('my_tours.tour_card.continue_tour', tourLng) : t('my_tours.tour_card.start_tour', tourLng)}
											</Button>
										)}

										{/* Download Button - mobile only, hidden if fresh download or offline+not-downloaded */}
										{!isDesktop && !(isOfflineReady && !isTourDownloadStale(tourId)) && !(isOffline && !isOfflineReady) && (
											<Button
												fullWidth
												variant="outlined"
												onClick={() => handleDownloadTourClick(tour)}
												startIcon={<DownloadIcon sx={{ mx: 1 }} />}
												sx={{
													fontSize: 14,
													fontWeight: 600,
													lineHeight: '16px',
													letterSpacing: 0,
													borderColor: mainColor,
													color: mainColor,
													borderRadius: '24px',
													py: 1,
													mt: 1,
													textTransform: 'none',
													boxShadow: 'none',
													transition: 'all 0.2s',
													flexDirection: isContentRTL ? 'row' : 'row-reverse',
													'&:hover': {
														borderColor: mainColor,
														backgroundColor: 'rgba(210, 85, 41, 0.04)',
													}
												}}
											>
												{t('pre_start_tour.pre_download_modal.download', tourLng)}
											</Button>
										)}
									</Box>
								</Box>
							);
						})}
					</Box>
				</Box>
			</Box>

			{/* iOS Install Guide Modal */}
			{showIosInstallGuide && (
				<Box
					sx={{
						position: 'fixed',
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						backgroundColor: 'rgba(0, 0, 0, 0.5)',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						zIndex: 200,
						animation: 'fadeIn 0.3s ease',
						'@keyframes fadeIn': {
							from: { opacity: 0 },
							to: { opacity: 1 },
						},
					}}
					onClick={() => setShowIosInstallGuide(false)}
				>
					<Box
						dir={isPageRTL ? 'rtl' : 'ltr'}
						sx={{
							backgroundColor: COLORS.surface,
							borderRadius: '16px',
							p: '24px',
							maxWidth: 340,
							width: '90%',
							textAlign: isPageRTL ? 'right' : 'left',
							position: 'relative',
							animation: 'scaleIn 0.3s ease',
							'@keyframes scaleIn': {
								from: { transform: 'scale(0.9)' },
								to: { transform: 'scale(1)' },
							},
						}}
						onClick={(e) => e.stopPropagation()}
					>
						{/* Close button */}
						<Box
							onClick={() => setShowIosInstallGuide(false)}
							sx={{
								position: 'absolute',
								top: 12,
								right: isPageRTL ? 'auto' : 12,
								left: isPageRTL ? 12 : 'auto',
								background: 'none',
								border: 'none',
								cursor: 'pointer',
								color: COLORS.textLight,
								p: '4px',
								lineHeight: 1,
								transition: 'color 0.2s',
								'&:hover': { color: COLORS.textDark },
							}}
						>
							<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
								<line x1="18" y1="6" x2="6" y2="18"></line>
								<line x1="6" y1="6" x2="18" y2="18"></line>
							</svg>
						</Box>

						<Typography sx={{ fontSize: 18, fontWeight: 700, color: COLORS.textDark, mb: 2 }}>
							{t('my_tours.pwa.ios_title')}
						</Typography>

						{/* Step 1 */}
						<Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2, }}>
							<Box
								sx={{
									width: 24,
									height: 24,
									borderRadius: '50%',
									backgroundColor: (generalColor || COLORS.primary),
									color: '#fff',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									fontSize: 13,
									fontWeight: 600,
									flexShrink: 0,
								}}
							>
								1
							</Box>
							<Box>
								<Typography sx={{ fontSize: 14, fontWeight: 600, color: COLORS.textDark, mb: 0.5 }}>
									{t('my_tours.pwa.ios_step1_title')}
								</Typography>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
									<Typography sx={{ fontSize: 13, color: COLORS.textMedium }}>
										{t('my_tours.pwa.ios_step1_description')}
									</Typography>
									{/* iOS Share icon */}
									<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={(generalColor || COLORS.primary)} strokeWidth="2">
										<path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path>
										<polyline points="16 6 12 2 8 6"></polyline>
										<line x1="12" y1="2" x2="12" y2="15"></line>
									</svg>
								</Box>
							</Box>
						</Box>

						{/* Step 2 */}
						<Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2, }}>
							<Box
								sx={{
									width: 24,
									height: 24,
									borderRadius: '50%',
									backgroundColor: (generalColor || COLORS.primary),
									color: '#fff',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									fontSize: 13,
									fontWeight: 600,
									flexShrink: 0,
								}}
							>
								2
							</Box>
							<Box>
								<Typography sx={{ fontSize: 14, fontWeight: 600, color: COLORS.textDark, mb: 0.5 }}>
									{t('my_tours.pwa.ios_step2_title')}
								</Typography>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
									<Typography sx={{ fontSize: 13, color: COLORS.textMedium }}>
										{t('my_tours.pwa.ios_step2_description')}
									</Typography>
									{/* Plus square icon */}
									<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={(generalColor || COLORS.primary)} strokeWidth="2">
										<rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
										<line x1="12" y1="8" x2="12" y2="16"></line>
										<line x1="8" y1="12" x2="16" y2="12"></line>
									</svg>
								</Box>
							</Box>
						</Box>

						{/* Step 3 */}
						<Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 3, }}>
							<Box
								sx={{
									width: 24,
									height: 24,
									borderRadius: '50%',
									backgroundColor: (generalColor || COLORS.primary),
									color: '#fff',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									fontSize: 13,
									fontWeight: 600,
									flexShrink: 0,
								}}
							>
								3
							</Box>
							<Box>
								<Typography sx={{ fontSize: 14, fontWeight: 600, color: COLORS.textDark, mb: 0.5 }}>
									{t('my_tours.pwa.ios_step3_title')}
								</Typography>
								<Typography sx={{ fontSize: 13, color: COLORS.textMedium }}>
									{t('my_tours.pwa.ios_step3_description')}
								</Typography>
							</Box>
						</Box>

						<Button
							fullWidth
							onClick={() => setShowIosInstallGuide(false)}
							sx={{
								backgroundColor: (generalColor || COLORS.primary),
								color: '#fff',
								py: 1.5,
								borderRadius: '12px',
								textTransform: 'none',
								fontSize: 15,
								fontWeight: 600,
								'&:hover': {
									backgroundColor: (generalColor || COLORS.primary),
									opacity: 0.9,
								},
							}}
						>
							{t('my_tours.pwa.got_it')}
						</Button>
					</Box>
				</Box>
			)}
		</Box>
	);
};

export default MyToursPage;
